import { useState } from 'react';
import { Box, Typography, TextField, Button, CircularProgress, Checkbox } from '@mui/material';
import { default as axios } from '../axiosConfig';
import { useNavigate } from 'react-router-dom';
import { API_RegisterUser } from '../Assets/ApiUrlContainer';
import { ModalPrompt } from './ModalPrompt';

const RegisterPage = () => {
	const [firstName, setFirstName] = useState('');
	const [lastName, setLastName] = useState('');
	const [email, setEmail] = useState('');
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState('');
	const [termsAgreed, setTermsAgreed] = useState(false);
	const [termsModalToggleState, setTermsModalToggleState] = useState(false);
	const navigate = useNavigate();

	// Regular expression for valid names (only letters and spaces)
	const nameRegex = /^[A-Za-z\s]+$/;

	const handleSubmit = async (e) => {
		e.preventDefault();
		setError('');

		// Validate first and last name format
		if (!nameRegex.test(firstName)) {
			setError('First name can only contain letters and spaces.');
			return;
		}
		if (!nameRegex.test(lastName)) {
			setError('Last name can only contain letters and spaces.');
			return;
		}

		if (!email.includes('@')) {
			setError('Enter a valid email address.');
			return;
		}

		try {
			setLoading(true);
			const response = await axios.post(API_RegisterUser, {
				firstName: firstName.trim(),
				lastName: lastName.trim(),
				email: email.trim().toLowerCase(),
			});

			setLoading(false);

			if (response.status === 200) {
				navigate('/thank-you-registration'); // Redirect to Thank You Page
			} else {
				setError(response.data.error || 'An error occurred.');
			}
		} catch (error) {
			setError(error.response?.data?.error || 'An error occurred. Please try again.');
		} finally {
			setLoading(false);
		}
	};

	const handleTermsModalOpened = () => setTermsModalToggleState(true);
	const handleTermsModalClosed = () => setTermsModalToggleState(false);

	return (
		<div className='registerPageContainer'>
			<Box
				sx={{
					height: '100%',
					display: 'flex',
					justifyContent: 'center',
					alignItems: 'center',
					flexDirection: 'column',
					backgroundColor: '#FAFAFA',
					textAlign: 'center',
				}}
			>
				<Box
					sx={{
						maxWidth: '420px',
						width: '100%',
						backgroundColor: 'white',
						padding: '24px',
						borderRadius: '8px',
						boxShadow: 3,
						textAlign: 'left',
						marginBottom: '98px'
					}}
				>
					<Typography variant='h5' sx={{ marginBottom: '20px', textAlign: 'center' }}>
						Register
					</Typography>
					<form onSubmit={handleSubmit}>
						<TextField
							label='First Name'
							variant='filled'
							fullWidth
							required
							value={firstName}
							onChange={(e) => setFirstName(e.target.value)}
							error={!nameRegex.test(firstName) && firstName !== ''}
							helperText={
								!nameRegex.test(firstName) && firstName !== '' ? 'Only letters and spaces are allowed' : ''
							}
							sx={{ marginBottom: '16px' }}
						/>
						<TextField
							label='Last Name'
							variant='filled'
							fullWidth
							required
							value={lastName}
							onChange={(e) => setLastName(e.target.value)}
							error={!nameRegex.test(lastName) && lastName !== ''}
							helperText={
								!nameRegex.test(lastName) && lastName !== '' ? 'Only letters and spaces are allowed' : ''
							}
							sx={{ marginBottom: '16px' }}
						/>
						<TextField
							label='Email'
							variant='filled'
							fullWidth
							required
							value={email}
							onChange={(e) => setEmail(e.target.value)}
							error={Boolean(error)}
							helperText={error}
							sx={{ marginBottom: '16px' }}
						/>

						<div className="checkBoxContainer">
							<Checkbox
								checked={termsAgreed}
								color="default"
								onChange={() => setTermsAgreed(!termsAgreed)}
								required={true}
								size="medium"
								sx={{ marginBottom: '1rem', paddingTop: '0', paddingBottom: '0' }} />

							<p className='termsAndConditionsText'>
								I read and agree to the <span className='modalBtn' onClick={handleTermsModalOpened}>terms and conditions</span>
							</p>
						</div>

						<Button
							type='submit'
							fullWidth
							variant='contained'
							sx={{ backgroundColor: '#673AB7', color: 'white', marginBottom: '12px' }}
							disabled={loading || !termsAgreed}
						>
							{loading ? <CircularProgress size={24} color='inherit' /> : 'Submit'}
						</Button>
					</form>
					<Button
						fullWidth
						variant='outlined'
						sx={{ color: '#673AB7', borderColor: '#673AB7' }}
						onClick={() => navigate('/')}
					>
						Return to Login
					</Button>
				</Box>
			</Box>

			<ModalPrompt
				onModalClose={handleTermsModalClosed}
				customModalContainerStyles={{ width: '85% !important' }}
				modalToggleState={termsModalToggleState}
				modalTitle={"Software License Agreement"}
				modalContent={
					<>
						<div className="licenseAgreementModal">
							<div className="effectiveDate">
								<b>Effective Date:</b> Jan 1, 2026
							</div>

							<div className='disclosure'>
								<b>PLEASE READ CAREFULLY.</b>
								<p>By clicking <b><q>Accept</q></b>, accessing, or using this cloud-based software (<q>Software</q>), you agree to be bound by this Software License Agreement (<q>Agreement</q>). If you do not agree, click <b><q>Decline</q></b> and do not use the Software.</p>
							</div>

							<div className='pointBlock'>
								<div className='pointHeader'>1. Grant of License</div>
								<div className='pointDescription'>
									Subject to your compliance with this Agreement, The Growth Spark (<q>Licensor</q>) grants you a <b>limited, non-exclusive, non-transferable, revocable license</b> to access and use the Software solely for your internal business or personal purposes.
								</div>
							</div>

							<div className='pointBlock'>
								<div className='pointHeader'>2. Account Responsibility</div>
								<div className='pointDescription'>
									You are responsible for:
									<ul>
										<li>Maintaining the confidentiality of your login credentials</li>
										<li>All activities conducted under your account</li>
										<li>Ensuring that your use complies with applicable laws and this Agreement</li>
									</ul>
								</div>
							</div>

							<div className='pointBlock'>
								<div className='pointHeader'>3. Acceptable Use</div>
								<div className='pointDescription'>
									You agree <b>not</b> to:
									<ul>
										<li>Reverse engineer, copy, or modify the Software</li>
										<li>Use the Software to violate laws or third-party rights</li>
										<li>Interfere with or disrupt the security or performance of the Software</li>
										<li>Share access credentials with unauthorized users</li>
									</ul>
								</div>
							</div>

							<div className='pointBlock'>
								<div className='pointHeader'>4. Ownership and Intellectual Property</div>
								<div className='pointDescription'>
									The Licensor owns the Software and all related intellectual property rights. No ownership rights are transferred to you under this Agreement.
								</div>
							</div>

							<div className='pointBlock'>
								<div className='pointHeader'>5. Cloud Services and Availability</div>
								<div className='pointDescription'>
									Licensor may update, modify, or discontinue features of the Software at any time. Licensor does not guarantee uninterrupted or error-free availability.
								</div>
							</div>

							<div className='pointBlock'>
								<div className='pointHeader'>6. Data and Privacy</div>
								<div className='pointDescription'>
									By using the Software, you consent to the collection and processing of data in accordance with the <b>Privacy Policy</b>. You retain ownership of your data, subject to Licensor&rsquo;s rights to operate the Software.
								</div>
							</div>

							<div className='pointBlock'>
								<div className='pointHeader'>7. Fees</div>
								<div className='pointDescription'>
									Use of this software requires payment. All fees are non-refundable unless otherwise required by law.
								</div>
							</div>

							<div className='pointBlock'>
								<div className='pointHeader'>8. Termination</div>
								<div className='pointDescription'>
									Licensor may suspend or terminate your access immediately if you violate this Agreement. Upon termination, your right to use the Software will cease.
								</div>
							</div>

							<div className='pointBlock'>
								<div className='pointHeader'>9. Disclaimer of Warranties</div>
								<div className='pointDescription'>
									THE SOFTWARE IS PROVIDED <b><q>AS IS</q></b> AND <b><q>AS AVAILABLE</q></b>, WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED.
								</div>
							</div>

							<div className='pointBlock'>
								<div className='pointHeader'>10. Limitation of Liability</div>
								<div className='pointDescription'>
									TO THE MAXIMUM EXTENT PERMITTED BY LAW, LICENSOR SHALL NOT BE LIABLE FOR INDIRECT, INCIDENTAL, OR CONSEQUENTIAL DAMAGES.
								</div>
							</div>

							<div className='pointBlock'>
								<div className='pointHeader'>11. Data Protection & Privacy</div>
								<div className='pointDescription'>
									<ul>
										<li><b>GDPR:</b> Personal data is processed lawfully, transparently, and only for legitimate business purposes.</li>
										<li><b>HIPAA (if applicable):</b> Protected Health Information (PHI) must only be accessed and used as permitted.</li>
										<li><b>SOC 2:</b> Security, availability, and confidentiality controls are enforced to protect system and customer data.</li>
									</ul>

									You retain ownership of your data. Use of the software is subject to our <b>Privacy Policy</b> and applicable data protection laws.
								</div>
							</div>

							<div className='pointBlock'>
								<div className='pointHeader'>12. Governing Law</div>
								<div className='pointDescription'>
									This Agreement shall be governed by and construed in accordance with the laws of British Columbia and the laws of Canada applicable therein. Any and all disputes arising under this Registration Rights Agreement, whether as to interpretation, performance or otherwise, shall be subject to the exclusive jurisdiction of the courts of the Province of British Columbia and each of the parties hereto hereby irrevocably attorns to the jurisdiction of the courts of British Columbia.
									<p>By clicking <b><q>Accept</q></b>, you acknowledge that you have read, understood, and agreed to this Agreement.</p>
								</div>
							</div>
						</div>
					</>
				}
			/>

		</div>
	);
};

export default RegisterPage;
